import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { BuildingWithConvertedAddresses } from '../utils/types';
import { centroidOf } from '../HouseMap/sideAddresses';
import './BuildingLocationMap.scss';

interface BuildingLocationMapProps {
  selectedBuilding: BuildingWithConvertedAddresses;
}

const BuildingLocationMap: React.FC<BuildingLocationMapProps> = ({ selectedBuilding }) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);

  // `building.centroid` is the CLICK POINT on the map-click path, not the
  // building's middle — derive the true centre from the footprint so the map
  // and marker sit on the building (mirrors the hero map). Fall back to
  // centroid, then the first address.
  const center =
    centroidOf(selectedBuilding.geometry) ||
    selectedBuilding.centroid ||
    selectedBuilding.addresses?.[0]?.coordinates;
  // Resolved outside the effect so it can depend on the coordinates themselves
  // rather than the `selectedBuilding` object: callers build that object inline
  // during render, so a new identity every parent render would tear down and
  // re-create the whole Mapbox instance — a visible map reload. (JAA-306 review)
  const lng = center?.lng;
  const lat = center?.lat;
  // Mapbox rejects non-finite values and latitudes outside the geographic
  // range. `centroidOf` receives backend geometry, so malformed coordinates
  // must fall through here rather than reach Map/Marker constructors.
  const hasCenter =
    typeof lng === 'number' &&
    Number.isFinite(lng) &&
    typeof lat === 'number' &&
    Number.isFinite(lat) &&
    lat >= -90 &&
    lat <= 90;

  useEffect(() => {
    if (!mapContainerRef.current || !hasCenter || lng === undefined || lat === undefined) return;

    // Initialize map
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN!;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: `mapbox://styles/jaakkocom/${process.env.REACT_APP_MAPBOX_STYLE_ID}`,
      center: [lng, lat],
      zoom: 15,
    });
    mapRef.current = map;

    // Add navigation controls
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Create a custom marker element
    const markerElement = document.createElement('div');
    markerElement.className = 'building-location-marker';
    markerElement.innerHTML = `
      <svg fill="none" height="40px" viewBox="0 0 24 24" width="40px" xmlns="http://www.w3.org/2000/svg" id="fi_11515653"><circle cx="12" cy="12" r="8" fill="white"/><path d="m12 0c-4.687 0-8.5 3.813-8.5 8.5 0 8.218 7.839 15.09 8.173 15.378.094.081.21.122.327.122s.233-.041.327-.122c.333-.289 8.173-7.161 8.173-15.378 0-4.687-3.813-8.5-8.5-8.5zm4.462 8.691c-.077.187-.26.309-.462.309h-.5v3.5c0 .276-.224.5-.5.5h-2c-.276 0-.5-.224-.5-.5v-2.5h-1v2.5c0 .276-.224.5-.5.5h-2c-.276 0-.5-.224-.5-.5v-3.5h-.5c-.202 0-.385-.122-.462-.309-.078-.187-.035-.402.108-.545l4-4c.195-.195.512-.195.707 0l4 4c.144.144.187.358.109.545z" fill="rgb(0,0,0)"></path></svg>
    `;

    // Add marker to map
    const marker = new mapboxgl.Marker(markerElement, {
      offset: [0, -12] // [x, y] offset in pixels - negative y moves marker up
    })
      .setLngLat([lng, lat])
      .addTo(map);
    markerRef.current = marker;

    // Add click handler to recenter map on marker
    const handleMarkerClick = () => {
      const markerPosition = marker.getLngLat();
      map.flyTo({
        center: [markerPosition.lng, markerPosition.lat],
        zoom: 16,
      });
    };
    markerElement.addEventListener('click', handleMarkerClick);

    // Mapbox measures its GL canvas from the container at init; if the container
    // height settles after mount (e.g. the desktop 400px map box, or a
    // mobile→desktop layout switch), the canvas keeps its stale size and leaves a
    // blank strip. Observe the container and resize the canvas to match.
    const resizeObserver = new ResizeObserver(() => {
      map.resize();
    });
    resizeObserver.observe(mapContainerRef.current);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
      markerElement.removeEventListener('click', handleMarkerClick);
      marker.remove();
      map.remove();
      if (markerRef.current === marker) markerRef.current = null;
      if (mapRef.current === map) mapRef.current = null;
    };
    // Coordinates are updated in place below. Recreate only when this component
    // gains or loses a usable centre, not for every related-card transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCenter]);

  useEffect(() => {
    if (!hasCenter || lng === undefined || lat === undefined) return;
    markerRef.current?.setLngLat([lng, lat]);
    mapRef.current?.jumpTo({ center: [lng, lat] });
  }, [hasCenter, lng, lat]);

  return (
    <div className="building-location-map-container">
      <div ref={mapContainerRef} className="building-location-map" />
    </div>
  );
};

export default BuildingLocationMap; 